import { hasFormChanges } from "components/FormControls/utils";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { useMessage } from "hooks/intl";
import { useEffect, useRef, useState } from "react";
import { Location, useBlocker, useNavigate } from "react-router-dom";

interface LeaveSiteGuardProps {
  warnBeforeRefresh?: boolean;
  isDirty?: boolean;
  initialValues?: Record<string, any>;
  currentValues?: Record<string, any>;
}

export function LeaveSiteGuard({
  warnBeforeRefresh,
  isDirty = false,
  initialValues,
  currentValues,
}: LeaveSiteGuardProps) {
  const { message } = useMessage();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nextLocation, setNextLocation] = useState("");
  const skipBlockerRef = useRef(false);

  // Check if form has actual changes by comparing values
  const hasRealChanges = (): boolean => {
    // If isDirty flag is provided, use it
    if (isDirty !== undefined) {
      // If we have initial and current values, do a comparison
      if (initialValues && currentValues) {
        return hasFormChanges(initialValues, currentValues);
      }
      return isDirty;
    }
    return false;
  };

  const blocker = (currentLocation: Location, nextLocation: Location) => {
    // Allow navigation if we're explicitly skipping the blocker
    if (skipBlockerRef.current) {
      skipBlockerRef.current = false;
      return false;
    }

    setNextLocation(nextLocation.pathname);
    if (
      nextLocation.pathname !== currentLocation.pathname.split("?")[0] &&
      hasRealChanges()
    ) {
      setIsModalOpen(true);
      return true;
    }
    return false;
  };

  useBlocker(
    (history: {
      currentLocation: Location<any>;
      nextLocation: Location<any>;
    }) => blocker(history.currentLocation, history.nextLocation),
  );

  const handleWarnBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasRealChanges()) {
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
    return undefined;
  };

  useEffect(() => {
    if (warnBeforeRefresh) {
      window.addEventListener("beforeunload", handleWarnBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleWarnBeforeUnload);
    };
    // eslint-disable-next-line
  }, [warnBeforeRefresh, initialValues, currentValues, isDirty]);

  // If no actual changes, don't render the modal
  if (!hasRealChanges()) {
    return null;
  }

  const handleDiscardChanges = () => {
    setIsModalOpen(false);
    // Set flag to bypass blocker for the next navigation
    skipBlockerRef.current = true;
    // Then navigate
    if (nextLocation) {
      navigate(nextLocation);
    }
  };

  return (
    <ConfirmationModal
      action={async () => {
        setIsModalOpen(false);
      }}
      submitText={message({ id: "backToEdit" })}
      close={() => setIsModalOpen(false)}
      additionalCloseAction={handleDiscardChanges}
      data-test-id="leave-confirmation"
      header="areYouSureYouWantToExit"
      info="changesWillBeLost"
      open={isModalOpen}
      cancelText={message({ id: "discardChangesAndExit" })}
      modalWidth={454}
    />
  );
}
